import React, { useEffect, useState } from "react";
import AuthLayout from "../../layouts/AuthLayout";
import authImage from "../../assets/img/auth/auth.png";
import logo from "../../assets/img/logo-full.png";
import { useForm } from "react-hook-form";
import { get, post } from "../../services/api";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import NotificationEventTypes from "../../constants/notificationEventsEnum";

function GuardianRegistration() {
  const token = localStorage.getItem("token") || "sample";
  const organizationId = process.env.REACT_APP_ORGANIZATION_ID;
  const [centersData, setCentersData] = useState([]);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm();

  const getCentersList = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(
        `Center/GetCenterByOrganizationId?organizationId=${organizationId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setCentersData(response?.center || []);
    } catch (error) {
      console.error("Error fetching Centers:", error);
    }
  };

  useEffect(() => {
    getCentersList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveData = async (data) => {
    const formData = {
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      organizationId: process.env.REACT_APP_ORGANIZATION_ID,
      centerId: data?.centerId,
      FormId: 22,
      NotificationEventTypeIds: NotificationEventTypes.ADD.toString(),
    };

    try {
      const response = await post("User/GuardianRegistration", formData);

      if (response.isSuccess) {
        toast.success(response.message);
        // optional: redirect to login after success
        // navigate("/login");
      } else {
        toast.error(response.message || "An unexpected error occurred");
      }
    } catch (error) {
      console.error("Error.", error);
      toast.error("Something went wrong.");
    }
  };

  const handleChangeOfInputWithLengthCheck = (fieldName) => (e) => {
    const value = e.target.value;
    setValue(fieldName, value, { shouldValidate: true });
  };

  // overlay style for loader
  const overlayStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255,255,255,0.8)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    borderRadius: 12,
  };

  return (
    <AuthLayout>
      <div className="row flex-center position-relative min-vh-100 g-0 py-5">
        <div className="col-11 col-sm-10 col-xl-10">
          <div
            className="card border border-translucent auth-card"
            style={{ position: "relative" }}
          >
            {/* Loader overlay */}
            {isSubmitting && (
              <div style={overlayStyle} aria-live="polite" aria-busy="true">
                <div className="text-center">
                  <div
                    className="spinner-border"
                    role="status"
                    style={{ width: 48, height: 48 }}
                  >
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <div
                    style={{ marginTop: 10, fontWeight: 600, color: "#6b7280" }}
                  >
                    Creating account...
                  </div>
                </div>
              </div>
            )}

            <div className="card-body pe-md-0">
              {/* 2-column layout: left image, right form (matches Login.js) */}
              <div className="row gx-0 gy-0 align-items-stretch">
                {/* LEFT: image (uses contain so no crop, background fills gaps) */}
                <div
                  className="col-md-6 d-none d-md-flex align-items-center justify-content-center"
                  style={{
                    background: "#f7f8fa",
                    borderTopLeftRadius: 12,
                    borderBottomLeftRadius: 12,
                    overflow: "hidden",
                    minHeight: 420,
                    padding: 0,
                  }}
                >
                  <img
                    src={authImage}
                    alt="Auth visual"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      backgroundColor: "#690505ff",
                    }}
                  />
                </div>

                {/* RIGHT: registration form */}
                <div className="col-12 col-md-6 d-flex align-items-center">
                  <div className="w-100 p-4 p-md-5">
                    <div className="text-center mb-4">
                      <div className="d-flex justify-content-center mb-3">
                        <img src={logo} alt="Logo" width="200" />
                      </div>
                      <h2 className="mb-0 fw-bold">Guardian Registration</h2>
                      <p className="text-muted">
                        Please fill the details to create guardian account.
                      </p>
                    </div>

                    <form className="row g-3" onSubmit={handleSubmit(saveData)}>
                      <div className="col-12">
                        <label className="form-label form-label-new">
                          Campus / Center <span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-select"
                          {...register("centerId", {
                            required: "This field is required.",
                          })}
                        >
                          <option value="">Select</option>
                          {centersData.map((item, index) => (
                            <option key={index} value={item.id}>
                              {item.name}
                            </option>
                          ))}
                        </select>
                        {errors.centerId && (
                          <small className="text-danger">
                            {errors.centerId.message}
                          </small>
                        )}
                      </div>

                      <div className="col-md-6">
                        <label className="form-label form-label-new">
                          Email Address <span className="text-danger">*</span>
                        </label>
                        <input
                          {...register("email", {
                            required: "This field is required.",
                            pattern: {
                              value:
                                /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                              message: "Please enter a valid email.",
                            },
                          })}
                          type="text"
                          className="form-control form-icon-input"
                        />
                        {errors.email && (
                          <small className="text-danger">
                            {errors.email.message}
                          </small>
                        )}
                      </div>

                      <div className="col-md-6">
                        <label className="form-label form-label-new">
                          First Name <span className="text-danger">*</span>
                        </label>
                        <input
                          {...register("firstName", {
                            required: "This field is required.",
                          })}
                          type="text"
                          className="form-control form-icon-input"
                          onChange={handleChangeOfInputWithLengthCheck(
                            "firstName"
                          )}
                        />
                        {errors.firstName && (
                          <small className="text-danger">
                            {errors.firstName.message}
                          </small>
                        )}
                      </div>

                      <div className="col-md-6">
                        <label className="form-label form-label-new">
                          Last Name <span className="text-danger">*</span>
                        </label>
                        <input
                          {...register("lastName", {
                            required: "This field is required.",
                          })}
                          type="text"
                          className="form-control form-icon-input"
                          onChange={handleChangeOfInputWithLengthCheck(
                            "lastName"
                          )}
                        />
                        {errors.lastName && (
                          <small className="text-danger">
                            {errors.lastName.message}
                          </small>
                        )}
                      </div>

                      <div className="col-12 mt-3">
                        <button
                          className="btn w-100 mb-1"
                          style={{
                            background: "#f59e0b",
                            borderColor: "#f59e0b",
                            color: "#fff",
                            padding: "12px 18px",
                            fontWeight: 700,
                            borderRadius: 8,
                          }}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <span
                              className="spinner-border spinner-custom-size me-1"
                              aria-hidden="true"
                            />
                          ) : null}
                          <span role="status">Create Account</span>
                        </button>

                        <div className="text-center mt-2">
                          <NavLink className="fs-9 fw-bold" to={"/login"}>
                            Back to Login
                          </NavLink>
                        </div>
                      </div>
                    </form>

                    <div
                      className="text-center mt-4"
                      style={{ fontSize: 12, color: "#6b7280" }}
                    >
                      <div style={{ fontWeight: 700 }}>
                        Manage Your School SMARTLY
                      </div>
                      <div>Email: info@inspirer.com.pk</div>
                      <div>
                        <a
                          href="https://www.inspirer.com.pk"
                          target="_blank"
                          rel="noreferrer"
                        >
                          www.inspirer.com.pk
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
                {/* END right */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}

export default GuardianRegistration;
